import {
  META_CAPI_NEW_COHORTS,
  type CapiEventStatus,
  type MetaCapiAccountConfig,
  type MetaCapiCohortSummaryItem,
  type MetaCapiEventLogItem,
} from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  UzCard,
  type UzColumn,
  UzDialog,
  UzPageHeader,
  UzPagination,
  UzSelect,
  UzSpinner,
  UzStack,
  UzTable,
  UzTabs,
} from '@utazzo/ui';
import { RefreshCw } from 'lucide-react';
import React, { useState } from 'react';

import {
  useMetaCapiCohortSummary,
  useMetaCapiConfig,
  useMetaCapiEvent,
  useMetaCapiEvents,
  useRetryMetaCapiEvent,
  useSendMetaCapiTestEvent,
} from '../use-meta-capi';

const STATUS_TONE: Record<CapiEventStatus, 'success' | 'destructive' | 'warning' | 'secondary'> = {
  SENT: 'success',
  FAILED: 'destructive',
  PENDING: 'warning',
  SKIPPED: 'secondary',
};

const PAGE_SIZE = 20;

const OverviewTab = (): React.JSX.Element => {
  const { data, isLoading } = useMetaCapiConfig();
  const sendTest = useSendMetaCapiTestEvent();

  if (isLoading) return <UzSpinner />;
  if (!data) return <p className="text-muted-foreground text-sm">Config unavailable.</p>;

  const accountColumns: ReadonlyArray<UzColumn<MetaCapiAccountConfig>> = [
    {
      id: 'accountId',
      header: 'Ad account id',
      cell: (r) => <span className="font-mono text-xs">{r.accountId}</span>,
    },
    { id: 'label', header: 'Label', cell: (r) => r.label ?? '-' },
    {
      id: 'datasetId',
      header: 'Dataset id',
      cell: (r) => <span className="font-mono text-xs">{r.datasetId ?? '-'}</span>,
    },
    {
      id: 'token',
      header: 'Access token',
      cell: (r) =>
        r.hasAccessToken ? (
          <UzBadge tone="success">set</UzBadge>
        ) : (
          <UzBadge tone="destructive">missing</UzBadge>
        ),
    },
    {
      id: 'test',
      header: 'Test code',
      cell: (r) => (r.testEventCode ? <UzBadge tone="secondary">on</UzBadge> : '-'),
    },
    {
      id: 'action',
      header: '',
      cell: (r) => (
        <UzButton
          size="sm"
          variant="outline"
          disabled={!r.hasAccessToken || !r.datasetId || sendTest.isPending}
          onClick={() => sendTest.mutate({ accountId: r.accountId })}
        >
          Send test event
        </UzButton>
      ),
    },
  ];

  return (
    <UzStack gap="md">
      <UzCard
        title="Status"
        description={`Push is ${data.enabled ? 'ENABLED' : 'disabled'} · Graph API ${data.apiVersion}`}
      >
        <p className="text-muted-foreground text-sm">
          Ad-account credentials + routing are managed in the Secrets Manager under the
          <span className="font-mono"> meta.capi.*</span> keys. This screen is read-only for those.
        </p>
      </UzCard>
      <UzCard
        title="Ad accounts"
        description="Discovered from the Secrets Manager. Tokens are never shown."
      >
        {data.accounts.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No accounts configured yet. Add{' '}
            <span className="font-mono">meta.capi.account.&lt;id&gt;.access_token</span> and
            <span className="font-mono"> .dataset_id</span> rows in the Secrets Manager.
          </p>
        ) : (
          <UzTable columns={accountColumns} data={data.accounts} getRowId={(r) => r.accountId} />
        )}
      </UzCard>
    </UzStack>
  );
};

interface MappingRow {
  readonly event: string;
  readonly when: string;
}

interface LostReasonRow {
  readonly event: string;
  readonly reasons: string;
}

const EVENT_ROWS: ReadonlyArray<MappingRow> = [
  {
    event: 'Lead',
    when: 'A lead enters the system (intake). Also the standing event for any still-open lead until it reaches a terminal state.',
  },
  { event: 'Converted', when: 'Lead is Won.' },
  {
    event: 'Qualified',
    when: "Lead is Lost with a 'qualified' reason (real prospect that did not close).",
  },
  { event: 'PartialQualified', when: "Lead is Lost with a 'partial' reason." },
  { event: 'NonQualified', when: 'Lead is Lost with a junk, never-reached or no reason.' },
];

const LOST_REASON_ROWS: ReadonlyArray<LostReasonRow> = [
  {
    event: 'Qualified',
    reasons:
      'Already Booked (+ (NC)), Better Price from Other Vendor, Plan Dropped, Late Follow up By Agent, Long Follow-up',
  },
  {
    event: 'PartialQualified',
    reasons:
      'Only Exploring (+ (NC)), Postpone / Program Cancelled, Postponed, Client Unresponsive, Budget with International lead category (Short Haul / Long Haul)',
  },
  {
    event: 'NonQualified',
    reasons:
      'Budget with Domestic lead category, First Time Lost, Self Booker, Solo Traveler, Lead Not Genuine / Spam, Fake Query, Wrong Number / Incorrect Number, Not Contactable Loss (Marked by System), No response after 7 follow-up attempts, Others, no reason set, any other or unmapped reason (default)',
  },
];

const EventMappingReference = (): React.JSX.Element => {
  const eventColumns: ReadonlyArray<UzColumn<MappingRow>> = [
    {
      id: 'event',
      header: 'Event',
      cell: (r) => <span className="font-medium">{r.event}</span>,
    },
    { id: 'when', header: 'When it is sent', cell: (r) => r.when },
  ];
  const reasonColumns: ReadonlyArray<UzColumn<LostReasonRow>> = [
    {
      id: 'event',
      header: 'Event',
      cell: (r) => <span className="font-medium">{r.event}</span>,
    },
    { id: 'reasons', header: 'Lost reasons', cell: (r) => r.reasons },
  ];

  return (
    <UzCard
      title="Event mapping (reference)"
      description="Read-only. The mapping is defined in code. Only leads that came from Meta (carry a Meta lead id) are sent."
    >
      <UzStack gap="md">
        <UzTable columns={eventColumns} data={EVENT_ROWS} getRowId={(r) => r.event} />
        <p className="text-muted-foreground text-sm">
          Open leads (Not Qualified, Proposal, Warm, Hot) stay on Lead. Their terminal event is
          pushed automatically once they are Won or Lost. Budget losses are split by haul via the
          lead category.
        </p>
        <UzTable columns={reasonColumns} data={LOST_REASON_ROWS} getRowId={(r) => r.event} />
      </UzStack>
    </UzCard>
  );
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'SKIPPED', label: 'Skipped' },
] as const;

// Cohort filter: the current (new) cohorts only, so ops can isolate e.g. just
// Converted. Legacy lowercase cohorts stay reachable via "All cohorts".
const COHORT_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All cohorts' },
  ...META_CAPI_NEW_COHORTS.map((c) => ({ value: c, label: c })),
];

/** Row shape for the cohort summary table (a synthetic Total row is appended). */
interface CohortSummaryRow extends MetaCapiCohortSummaryItem {
  readonly isTotal?: boolean;
}

const CohortSummaryCard = (): React.JSX.Element => {
  const { data, isLoading } = useMetaCapiCohortSummary();

  const rows = React.useMemo<ReadonlyArray<CohortSummaryRow>>(() => {
    const cohorts = data?.cohorts ?? [];
    if (cohorts.length === 0) return [];
    const sent = cohorts.reduce((acc, c) => acc + c.sent, 0);
    const total = cohorts.reduce((acc, c) => acc + c.total, 0);
    return [...cohorts, { cohort: 'Total', sent, total, isTotal: true }];
  }, [data]);

  const columns: ReadonlyArray<UzColumn<CohortSummaryRow>> = [
    {
      id: 'cohort',
      header: 'Cohort',
      cell: (r) => <span className={r.isTotal ? 'font-semibold' : 'font-medium'}>{r.cohort}</span>,
    },
    {
      id: 'sent',
      header: 'Sent successfully',
      cell: (r) => (
        <span className={r.isTotal ? 'font-semibold' : 'font-medium text-emerald-700'}>
          {r.sent.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      id: 'total',
      header: 'Total attempts',
      cell: (r) => (
        <span className={r.isTotal ? 'font-semibold' : 'text-muted-foreground'}>
          {r.total.toLocaleString('en-IN')}
        </span>
      ),
    },
  ];

  return (
    <UzCard
      title="Cohorts sent to Meta"
      description="Successfully-delivered events per cohort (current implementation only). All-time totals."
    >
      {isLoading ? (
        <UzSpinner />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">No cohort events recorded yet.</p>
      ) : (
        <UzTable columns={columns} data={rows} getRowId={(r) => r.cohort} />
      )}
    </UzCard>
  );
};

const DeliveryLogTab = (): React.JSX.Element => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>('');
  const [cohort, setCohort] = useState<string>('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const query = {
    page,
    pageSize: PAGE_SIZE,
    sortDir: 'desc' as const,
    ...(status ? { status: status as CapiEventStatus } : {}),
    ...(cohort ? { capiEventName: cohort } : {}),
  };
  const { data, isLoading, isFetching, refetch } = useMetaCapiEvents(query);
  const retry = useRetryMetaCapiEvent();
  const detail = useMetaCapiEvent(detailId);

  const columns: ReadonlyArray<UzColumn<MetaCapiEventLogItem>> = [
    {
      id: 'created',
      header: 'When',
      cell: (r) => (
        <span className="text-muted-foreground text-xs">
          {new Date(r.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      id: 'event',
      header: 'Event',
      cell: (r) => <span className="font-medium">{r.capiEventName}</span>,
    },
    {
      id: 'account',
      header: 'Account',
      cell: (r) => <span className="font-mono text-xs">{r.adAccountId ?? '-'}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <UzBadge tone={STATUS_TONE[r.status]}>
          {r.status.charAt(0) + r.status.slice(1).toLowerCase().replace(/_/g, ' ')}
          {r.skipReason ? ` · ${r.skipReason}` : ''}
        </UzBadge>
      ),
    },
    { id: 'attempts', header: 'Tries', cell: (r) => r.attempts },
    {
      id: 'lead',
      header: 'Lead',
      cell: (r) => (
        <div className="flex flex-col">
          <span className="text-xs font-medium">{r.leadReference ?? '-'}</span>
          <span className="text-muted-foreground font-mono text-[10px]" title={r.leadId}>
            {r.leadId}
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (r) => (
        <div className="flex justify-end gap-2">
          <UzButton size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>
            View
          </UzButton>
          {(r.status === 'FAILED' || r.status === 'SKIPPED') && (
            <UzButton
              size="sm"
              variant="outline"
              disabled={retry.isPending}
              onClick={() => retry.mutate(r.id)}
            >
              Retry
            </UzButton>
          )}
        </div>
      ),
    },
  ];

  return (
    <UzStack gap="md">
      <CohortSummaryCard />
      <div className="flex items-center gap-2">
        <div className="w-56">
          <UzSelect
            value={cohort}
            onValueChange={(v) => {
              setCohort(v);
              setPage(1);
            }}
            options={COHORT_FILTER_OPTIONS}
          />
        </div>
        <div className="w-56">
          <UzSelect
            value={status}
            onValueChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={STATUS_FILTER_OPTIONS as ReadonlyArray<{ value: string; label: string }>}
          />
        </div>
        <UzButton
          size="sm"
          variant="outline"
          onClick={() => void refetch()}
          loading={isFetching}
          disabled={isFetching}
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Refresh
        </UzButton>
      </div>
      {isLoading ? (
        <UzSpinner />
      ) : (
        <>
          <UzTable columns={columns} data={data?.data ?? []} getRowId={(r) => r.id} />
          {data && (
            <UzPagination
              page={data.pagination.page}
              pageSize={data.pagination.pageSize}
              totalItems={data.pagination.totalItems}
              onPageChange={setPage}
            />
          )}
        </>
      )}
      <UzDialog
        open={detailId !== null}
        onOpenChange={(open) => !open && setDetailId(null)}
        title="CAPI delivery detail"
        size="2xl"
        description={detail.data ? detail.data.capiEventName : ''}
      >
        {detail.isLoading ? (
          <UzSpinner />
        ) : (
          <UzStack gap="sm" className="w-full min-w-0">
            {detail.data?.eventId && (
              <p className="text-xs break-all">
                <span className="text-(--fg-muted)">event_id:</span>{' '}
                <span className="font-mono">{detail.data.eventId}</span>
              </p>
            )}
            {detail.data?.fbTraceId && (
              <p className="text-xs break-all">
                <span className="text-(--fg-muted)">fbtrace_id:</span>{' '}
                <span className="font-mono">{detail.data.fbTraceId}</span>
              </p>
            )}
            {detail.data?.error && (
              <p className="text-destructive text-xs break-words">{detail.data.error}</p>
            )}
            <div className="w-full min-w-0">
              <p className="mb-1 text-xs font-medium">Request payload (PII already hashed)</p>
              <pre className="bg-muted max-h-72 w-full min-w-0 overflow-auto rounded-md p-3 font-mono text-xs [overflow-wrap:anywhere] whitespace-pre-wrap">
                {JSON.stringify(detail.data?.requestPayload ?? {}, null, 2)}
              </pre>
            </div>
            {detail.data?.responseBody && (
              <div className="w-full min-w-0">
                <p className="mb-1 text-xs font-medium">Response</p>
                <pre className="bg-muted max-h-48 w-full min-w-0 overflow-auto rounded-md p-3 font-mono text-xs [overflow-wrap:anywhere] whitespace-pre-wrap">
                  {detail.data.responseBody}
                </pre>
              </div>
            )}
          </UzStack>
        )}
      </UzDialog>
    </UzStack>
  );
};

/**
 * Meta CAPI admin (ADR-0066). Read-only config status + static event mapping
 * reference + delivery log with retry. Credentials/routing are edited in the Secrets Manager.
 */
export const MetaCapiPage = (): React.JSX.Element => {
  const [tab, setTab] = useState('overview');
  return (
    <div className="flex flex-col gap-6">
      <UzPageHeader
        title="Meta CAPI"
        description="Server-side conversion events pushed to Meta as leads move through the funnel."
      />
      <UzTabs
        value={tab}
        onValueChange={setTab}
        tabs={[
          { value: 'overview', label: 'Overview', content: <OverviewTab /> },
          { value: 'mapping', label: 'Event mapping', content: <EventMappingReference /> },
          { value: 'log', label: 'Delivery log', content: <DeliveryLogTab /> },
        ]}
      />
    </div>
  );
};
